import { Dialog, DialogPanel } from '@headlessui/react'
import { useEffect, useState } from 'react'

import { useFunnelModal } from '../contexts/FunnelModalContext'
import { appendExperimentParams } from '../experiments'
import {
  BUILD_ENV,
  leadTierEvent,
  trackAdsConversion,
  trackDataLayerEvent
} from '../utils'

const LOCALES = ['de', 'en']

// FunnelModal wird in wrapRootElement gerendert — oberhalb des i18n-Contexts
// von gatsby-plugin-react-i18next (wrapPageElement). useI18next() liefert hier
// also keine Sprache. Stattdessen leiten wir die Sprache aus dem URL-Pfad ab:
// /en/... -> 'en', alles andere (Default-Sprache ohne Prefix, /de/...) -> 'de'.
const getLanguageFromPath = () => {
  if (typeof window === 'undefined') return 'de'
  const seg = window.location.pathname.split('/').filter(Boolean)[0]
  return LOCALES.includes(seg) ? seg : 'de'
}

// Auf /lp/<slug>/ (bzw. /en/lp/<slug>/) steckt die Kampagne im Pfad statt im
// Query-String. Ohne dieses Auslesen verlöre der Funnel die Zuordnung, sobald
// die Ads von ?variant= auf die Pfad-URLs umgestellt sind.
const getVariantFromPath = () => {
  if (typeof window === 'undefined') return ''
  const segments = window.location.pathname.split('/').filter(Boolean)
  const at = segments.indexOf('lp')
  return at === -1 ? '' : segments[at + 1] || ''
}

const dashboardFunnelBase = () => {
  // Local override — set GATSBY_DASHBOARD_URL=http://localhost:3000 in .env.development
  // to point the funnel iframe at a local dashboard dev server.
  if (process.env.GATSBY_DASHBOARD_URL) {
    return `${process.env.GATSBY_DASHBOARD_URL.replace(/\/$/, '')}/funnel`
  }
  if (BUILD_ENV === 'production') return 'https://dashboard.aivy.app/funnel'
  if (BUILD_ENV === 'staging')
    return 'https://staging.dashboard.aivy.app/funnel'
  return 'https://develop.dashboard.aivy.app/funnel'
}

const FunnelModal = () => {
  const { isOpen, closeFunnel } = useFunnelModal()
  const base = dashboardFunnelBase()
  const [src, setSrc] = useState(`${base}?language=${getLanguageFromPath()}`)

  // Re-key the iframe each time we open so the funnel resets cleanly.
  const [iframeKey, setIframeKey] = useState(0)

  useEffect(() => {
    if (!isOpen) return
    const params = new URLSearchParams(window.location.search)
    params.set('language', getLanguageFromPath())

    // Ein explizites ?variant= in der URL gewinnt gegen den Pfad.
    const pathVariant = getVariantFromPath()
    if (pathVariant && !params.has('variant'))
      params.set('variant', pathVariant)

    // Besucher-ID und LP-Zuteilungen (vid, exp_<key>), damit der Funnel
    // dieselbe Gruppe sieht. Alle anderen Parameter bleiben unveraendert.
    appendExperimentParams(params)

    setSrc(`${base}?${params.toString()}`)
    setIframeKey((k) => k + 1)
  }, [isOpen, base])

  useEffect(() => {
    if (!isOpen) return
    const handleMessage = (event) => {
      const type = event.data?.type

      if (type === 'AIVY_FUNNEL_EMAIL_SUBMITTED') {
        trackDataLayerEvent('generate_lead', {
          user_data: { email: event.data.email }
        })
      }

      if (type === 'AIVY_FUNNEL_Q1_COMPLETED') {
        trackDataLayerEvent('lead_engagement')
      }

      if (type === 'AIVY_FUNNEL_COMPLETE') {
        const { email, score } = event.data
        const tierEvent = leadTierEvent(score)
        trackAdsConversion({ eventName: tierEvent, email, score })
      }

      if (type === 'AIVY_FUNNEL_OPENED_LOGIN') {
        closeFunnel()
      }
      if (type === 'AIVY_FUNNEL_CLOSE_REQUESTED') {
        closeFunnel()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isOpen, closeFunnel])

  return (
    <Dialog
      open={isOpen}
      onClose={closeFunnel}
      className="relative z-[100] font-firasans"
    >
      {/* Blurry backdrop on the LANDING page — visible around the funnel's own modal card */}
      <div
        className="fixed inset-0 bg-dark-black/55 backdrop-blur-md"
        aria-hidden="true"
      />
      {/* Full-viewport panel hosting only the iframe — no extra chrome.
          The iframe page (/funnel) renders the actual modal card (rounded
          white surface). Its built-in backdrop becomes transparent in iframe
          context so this page's blur shows through. */}
      <DialogPanel className="fixed inset-0 flex items-center justify-center">
        <iframe
          key={iframeKey}
          src={src}
          title="Aivy Funnel"
          className="h-full w-full border-0"
          style={{ background: 'transparent', colorScheme: 'normal' }}
          allow="clipboard-write"
        />
      </DialogPanel>
    </Dialog>
  )
}

export default FunnelModal
